import React, { useEffect, useState } from "react";
import { Modal, Form, Spinner, Row } from "react-bootstrap";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

import "./HubzuContactUsBrokerModal.css";
import { submitBrokerContactUsForm } from "@/api/contactUs";
import { validateForm } from "@/utils/formValidator";
import { TextFormatter } from "@/components/sell/utils/TextFormatter";
import { useSelector } from "react-redux";

const HubzuContactUsBrokerModal = ({ cmsData, isModalOpen, handleModal }) => {
  const user = useSelector((state) => state.user.user);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'

  const handleValueChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^[0-9]*$/.test(value)) return;
    setFormData({ ...formData, [name]: value });

    // Clear errors on input change
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    // Validate on change
    const fieldError = validateForm({ [name]: value }, [name])[name];
    if (fieldError) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));
    }
  };

  const handleClear = () => {
    // Close the modal and clear the form after success
    handleModal();
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setTimeout(() => {
      setMessage("");
      setFormErrors({});
    }, 4000);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm(formData, Object.keys(formData));
    if (Object.keys(formErrors).length > 0) {
      setFormErrors(formErrors);
      return;
    }

    setLoading(true);
    setMessage(""); // Clear any previous message

    try {
      let response = await submitBrokerContactUsForm(formData);

      if (response?.status === "FAILURE") {
        setMessage(
          response?.message || "Unable to Save the lead. Please try Again."
        );
        setMessageType("error");
        return;
      }
      setMessage(
        "Thank you. Our customer care representative will reach out to you shortly."
      );
      setMessageType("success");
      handleClear();
    } catch (error) {
      setMessage("There was an error submitting the form. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
      // Hide the message after 4 seconds
      setTimeout(() => setMessage(""), 4000);
    }
  };

  const handleLinkClick = (e) => {
    console.log(e);
    if (e.includes("#Terms") || e.includes("#&") || e.includes("#Conditions")) {
      window.open("/about/terms", "_blank");
    } else if (e.includes("#Privacy") || e.includes("#Policy.")) {
      window.open("/Privacy-Policy/", "_blank");
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.userFirstName || "",
        lastName: user.userLastName || "",
        email: user.userEmailId || "",
        phone: user.primaryPhone || "",
      });
    }
  }, [user]);

  return (
    <>
      {/* Success/Error Message */}
      {message && (
        <div
          className={`message ${messageType} contact-us-broker-modal-container`}
        >
          <div className="contact-us-broker-modal-message">
            <span className="message-icon me-2 mt-1">
              {messageType === "success" ? (
                <FaCheckCircle size={20} />
              ) : (
                <FaTimesCircle size={20} color="red" />
              )}
            </span>
            {message}
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      <Modal show={isModalOpen} onHide={handleModal} centered size="md">
        <Modal.Header
          closeButton
          className="contact-us-broker-modal-header-cont"
        >
          <Modal.Title className="contact-us-broker-modal-header">
            {cmsData?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem 3rem" }}>
          <p className="contact-us-broker-modal-para">{cmsData?.description}</p>

          <Form onSubmit={handleFormSubmit}>
            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleValueChange}
              />
              {formErrors.firstName && (
                <div className="text-danger small mt-2">
                  {formErrors.firstName}
                </div>
              )}
            </div>

            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleValueChange}
              />
              {formErrors.lastName && (
                <div className="text-danger small mt-2">
                  {formErrors.lastName}
                </div>
              )}
            </div>

            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleValueChange}
              />
              {formErrors.email && (
                <div className="text-danger small mb-4">{formErrors.email}</div>
              )}
            </div>
            <div className="mb-4">
              <Form.Control
                className="mb-2 border-0 bg-light"
                placeholder="Phone"
                name="phone"
                type="numeric"
                maxLength={10}
                value={formData.phone}
                onChange={handleValueChange}
              />
              {formErrors.phone && (
                <div className="text-danger small mb-4">{formErrors.phone}</div>
              )}
            </div>

            <button
              className="btn btn-primary w-100"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Submit"
              )}
              {loading && " Submitting..."}
            </button>
            <p className="mt-3 small contact-us-broker-modal-tc">
              <p className="mt-3 small contact-us-com-modal-tc">
                <TextFormatter
                  text={cmsData?.termsAndConditions}
                  onLinkClick={handleLinkClick}
                  style={{
                    lineHeight: "20px",
                    fontSize: "14px",
                  }}
                />
              </p>
            </p>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default HubzuContactUsBrokerModal;
